import type { AiAnnotationDraft, AiProposalEngine } from "@/ai/engines/engine.types";
import { averageConfidence, firstSentence, inferProposalTimeSec, proposalHasKeyword } from "@/ai/engines/engineUtils";

const INTENT_KEYWORDS = ["intent", "tone", "variant", "mood", "goal", "emotion"];

export const intentEngine: AiProposalEngine = (context) => {
  const drafts: AiAnnotationDraft[] = [];
  const confidence = averageConfidence(context.proposals);
  const evidence = context.proposals.map((proposal) => proposal.rationale?.trim()).filter(Boolean) as string[];

  if (context.narrativeSummary.trim()) {
    drafts.push({
      type: "intent",
      subtype: "intent_inference",
      severity: confidence != null && confidence >= 0.8 ? "warning" : "suggestion",
      summary: firstSentence(context.narrativeSummary, "Intent inference ready for review."),
      details: {
        inferredIntent: context.narrativeSummary,
        confidence,
        supportingEvidence: evidence.slice(0, 3),
      },
      proposedAction: "review",
      nodeId: `narrative-summary:${context.selectedClipId ?? "global"}`,
      timeSec: context.anchorTimeSec,
      frame: context.anchorFrame,
      action: "Review inferred editorial intent",
    });
  }

  for (const proposal of context.proposals) {
    if (!proposalHasKeyword(proposal, INTENT_KEYWORDS)) continue;

    const variantLike = proposalHasKeyword(proposal, ["variant", "alternate"]);
    const timeSec = inferProposalTimeSec(context, proposal);
    drafts.push({
      type: "intent",
      subtype: variantLike ? "intent_variant" : "intent_drift",
      severity: variantLike ? "suggestion" : "warning",
      summary: proposal.rationale.trim() || "Intent review recommended.",
      details: variantLike
        ? {
            variantId: proposal.id,
            emotionalShift: context.narrativeSummary || null,
            visualOrAudioChanges: proposal.diffs.map((diff) => diff.kind),
            whyItWorks: proposal.rationale,
          }
        : {
            expectedIntent: context.narrativeSummary || null,
            observedTone: proposal.rationale || null,
            suggestedFix: "align_with_director_summary",
          },
      proposedAction: variantLike ? "apply_variant" : "review",
      nodeId: proposal.id,
      timeSec,
      frame: Math.floor(timeSec * context.fps),
      action: variantLike ? "Review alternate intent variant" : "Review intent drift",
    });
  }

  return drafts;
};